import { Button, DatePicker, Modal, Responsive } from '@/components/basic';
import { SelectSingleUser } from '@/components/partial/SelectSingleUser';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import OfficeFilterDropdown from '@/components/util/OfficeFilterDropdown';
import { api } from '@/lib/api/api';
import { VisitorFormType, schemaVisitor } from '@/lib/validation/visitor';
import { IVisitor } from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import FormErrorMessage from '../basic/FormMessages/FormErrorMessage';

type Props = { closeModal: () => void; visit?: IVisitor };

const defaultExpectedDate = addDays(new Date(), 2);
defaultExpectedDate.setHours(0, 0, 0, 0);

export function InviteVisitorForm({ closeModal, visit }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!visit;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const offices = authenticatedUser.offices?.offices ?? [];
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<VisitorFormType>({
    // TODO: Replace with Zod
    // resolver: yupResolver(schemaVisitor),
    defaultValues: !isEdit
      ? {
          name: '',
          email: '',
          phone: '',
          expected: defaultExpectedDate,
          office_id: offices[0]?.id,
          host_id: authenticatedUser.user?.id ?? '',
          reason: ''
        }
      : {
          name: visit.visitor.name,
          email: visit.visitor.email,
          phone: visit.visitor.phone,
          expected: visit.visit.expected
            ? parseISO(visit.visit.expected + `T00:00:00`)
            : undefined,
          office_id: visit.office.id,
          host_id: visit.host.id,
          reason: visit?.visit.reason ?? ''
        }
  });
  const submitMutation = useMutation(
    async (data: VisitorFormType) => {
      setLoading(true);
      const formattedData = {
        ...data,
        expected: data.expected
          ? format(data.expected, 'yyyy-MM-dd')
          : undefined,
        comment: '',
        picture: ''
      };

      visit?.visit.id
        ? await api.client.stations.updateGuest(visit.visit.id, formattedData)
        : await api.client.stations.registerGuest(
            data.office_id,
            formattedData
          );
    },
    {
      onSuccess: () => {
        setLoading(false);
        toast.success('Invitation saved');
        closeModal();
      },
      onSettled: async () => {
        setLoading(false);
        await queryClient.invalidateQueries(['stations.stationVisits']);
      }
    }
  );

  const onVisitorSubmit = async (data: VisitorFormType) => {
    submitMutation.mutate(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onVisitorSubmit)}>
        <div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col space-y-4">
              <div>
                <FormErrorMessage errors={errors} fieldName={'name'} />
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <Responsive.Label
                      filled={field.value.length > 0}
                      text="Visitor Name"
                      htmlFor="name"
                    >
                      <Responsive.Input
                        readOnly={isEdit}
                        type="text"
                        id="name"
                        {...field}
                      />
                    </Responsive.Label>
                  )}
                />
              </div>
              <div>
                <FormErrorMessage errors={errors} fieldName={'expected'} />
                <Controller
                  control={control}
                  name="expected"
                  render={({ field: { onChange, value } }) => (
                    <Responsive.Label
                      filled={true}
                      text="Expected Visit Date"
                      htmlFor="expected"
                    >
                      <DatePicker
                        selected={value}
                        name="expected"
                        className="input w-full rounded-md"
                        calendarClassName="tailwind-datepicker"
                        onChange={onChange}
                      />
                    </Responsive.Label>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col space-y-4">
              <div>
                <FormErrorMessage errors={errors} fieldName={'email'} />
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <Responsive.Label
                      filled={true}
                      text="Visitor Email"
                      htmlFor="email"
                    >
                      <Responsive.Input
                        readOnly={isEdit}
                        type="text"
                        id="email"
                        {...field}
                      />
                    </Responsive.Label>
                  )}
                />
              </div>
              <div>
                <FormErrorMessage errors={errors} fieldName={'reason'} />
                <Controller
                  control={control}
                  name="reason"
                  render={({ field }) => (
                    <Responsive.Label
                      filled={true}
                      text="Visitor Reason"
                      htmlFor="reason"
                    >
                      <Responsive.Input type="text" id="reason" {...field} />
                    </Responsive.Label>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col space-y-4">
              <div>
                <FormErrorMessage errors={errors} fieldName={'host'} />
                <Controller
                  control={control}
                  name="host_id"
                  render={({ field }) => (
                    <Responsive.Label filled={true} text="Host" htmlFor="host">
                      <SelectSingleUser
                        className="w-full"
                        onChange={(x) => field.onChange(x)}
                        value={field.value}
                      />
                    </Responsive.Label>
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col space-y-4">
              <Controller
                control={control}
                name="office_id"
                defaultValue={authenticatedUser.officeId ?? ''}
                render={({ field: { onChange, value } }) => (
                  <Responsive.Label
                    filled={true}
                    text="Office"
                    htmlFor="office_id"
                  >
                    <OfficeFilterDropdown
                      className="-mt-1 max-w-sm"
                      setOfficeId={(id) => {
                        onChange(id);
                      }}
                      officeId={value}
                      offices={offices}
                    />
                  </Responsive.Label>
                )}
              />
            </div>
          </div>
        </div>
        <Modal.Footer>
          <div className="">
            <Button
              className="w-full rounded-3xl sm:rounded-md"
              layout="squared"
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner color="text-white" />
              ) : isEdit ? (
                'Save'
              ) : (
                'Invite'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </>
  );
}
